import { useCallback, useMemo } from 'react'
import { collection, doc, query, where, serverTimestamp, deleteField } from 'firebase/firestore'
import type { Query } from 'firebase/firestore'
import { fbt } from 'fbtee'
import { db } from '../lib/firebase'
import { addDoc, updateDoc, deleteDoc } from '../lib/firestore'
import { runMutation } from '../lib/firestoreMutation'
import { useFirestoreCollection } from './useFirestore'
import { track, type ScheduleSurface } from '../lib/analytics'
import type { AudienceValue } from './useAudienceSelection'
import { SCHEDULE_MARKER_TIME_RE, type ScheduleMarker } from '@shared/firestore'

/** Who a line reaches: only its author, or a crew audience (empty = everyone). */
export type MarkerAudience = { isPrivate: true } | ({ isPrivate: false } & AudienceValue)

export interface MarkerInput {
  time: string
  label: string
  audience: MarkerAudience
}

function errorNoun() {
  return fbt("Couldn't save the schedule line", 'Error toast when saving, moving or deleting a custom schedule line fails')
}

// Defensive read: lines written by an older client (or hand-edited) may lack
// the newer fields, and a malformed time can't be placed on the grid.
function normalize(rows: ScheduleMarker[]): ScheduleMarker[] {
  return rows
    .filter(m => typeof m.day === 'string' && typeof m.time === 'string' && SCHEDULE_MARKER_TIME_RE.test(m.time))
    .map(m => ({ ...m, isPrivate: m.isPrivate !== false, viewerIds: m.viewerIds ?? [], label: m.label ?? '' }))
}

// Audience fields as written (schedule lines and map pins): a private doc or "everyone" clears the
// restriction fields so the trigger reads the doc the same way the picker does.
export function audienceFields(audience: MarkerAudience, clearMissing: boolean) {
  const clear = clearMissing ? deleteField() : undefined
  const restricted = !audience.isPrivate && (audience.audienceGroupIds !== undefined || audience.audienceFriends !== undefined)
  const fields: Record<string, unknown> = {
    isPrivate: audience.isPrivate,
    audienceGroupIds: restricted ? (audience as AudienceValue).audienceGroupIds ?? [] : clear,
    audienceFriends: restricted ? (audience as AudienceValue).audienceFriends ?? false : clear,
  }
  return Object.fromEntries(Object.entries(fields).filter(([, v]) => v !== undefined))
}

/**
 * The viewer's own schedule lines for an event, plus the ones friends shared
 * with them (via `viewerIds`, kept by the syncScheduleMarkerViewers trigger).
 * Hiding and block filtering are the caller's job.
 */
export function useScheduleMarkers(userId: string | null, eventId: string, surface: ScheduleSurface) {
  const ownQuery = useMemo(
    () =>
      userId
        ? (query(
            collection(db, 'scheduleMarkers'),
            where('userId', '==', userId),
            where('eventId', '==', eventId),
          ) as Query<ScheduleMarker>)
        : null,
    [userId, eventId],
  )
  const sharedQuery = useMemo(
    () =>
      userId
        ? (query(
            collection(db, 'scheduleMarkers'),
            where('eventId', '==', eventId),
            where('viewerIds', 'array-contains', userId),
          ) as Query<ScheduleMarker>)
        : null,
    [userId, eventId],
  )
  const { data: ownRows, loading: ownLoading } = useFirestoreCollection(ownQuery)
  const { data: sharedRows, loading: sharedLoading } = useFirestoreCollection(sharedQuery)

  const own = useMemo(() => normalize(ownRows), [ownRows])
  const shared = useMemo(() => normalize(sharedRows).filter(m => m.userId !== userId), [sharedRows, userId])

  const addMarker = useCallback(
    async (day: string, { time, label, audience }: MarkerInput) => {
      if (!userId) return
      await runMutation(errorNoun(), async () => {
        await addDoc(collection(db, 'scheduleMarkers'), {
          userId,
          eventId,
          day,
          time,
          label,
          ...audienceFields(audience, false),
          viewerIds: [],
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        })
        track('schedule_marker_created', { event_id: eventId, surface, kind: audience.isPrivate ? 'private' : 'shared' })
      })
    },
    [userId, eventId, surface],
  )

  const updateMarker = useCallback(
    async (id: string, { time, label, audience }: MarkerInput) => {
      await runMutation(errorNoun(), async () => {
        await updateDoc(doc(db, 'scheduleMarkers', id), {
          time,
          label,
          ...audienceFields(audience, true),
          updatedAt: serverTimestamp(),
        })
        track('schedule_marker_updated', { event_id: eventId, surface, kind: audience.isPrivate ? 'private' : 'shared' })
      })
    },
    [eventId, surface],
  )

  const moveMarker = useCallback(
    async (id: string, time: string) => {
      await runMutation(errorNoun(), async () => {
        await updateDoc(doc(db, 'scheduleMarkers', id), { time, updatedAt: serverTimestamp() })
        track('schedule_marker_moved', { event_id: eventId, surface })
      })
    },
    [eventId, surface],
  )

  const deleteMarker = useCallback(
    async (id: string) => {
      await runMutation(errorNoun(), async () => {
        await deleteDoc(doc(db, 'scheduleMarkers', id))
        track('schedule_marker_deleted', { event_id: eventId, surface })
      })
    },
    [eventId, surface],
  )

  return {
    own,
    shared,
    loading: ownLoading || sharedLoading,
    addMarker,
    updateMarker,
    moveMarker,
    deleteMarker,
  }
}
